<template>
  <PageHeader :showBreadcrumb="true" />
  <form v-if="authStore.isAdminUser && appStore.networks.length > 0 && !loading" @submit.stop.prevent="onSubmit">
    <div class="container">
      <DataTable v-model:editingRows="editingRows" v-model:filters="filters" :value="entities"
                 editMode="row" dataKey="id" @row-edit-save="onRowEditSave" @row-edit-init="onRowEditInit" @row-edit-cancel="onRowEditCancel"
                 stripedRows showGridlines paginator :rows="25"
                 sortField="createdAt" :sortOrder="-1" :loading="loading"
                 :pt="{
                   table: { style: 'min-width: 50rem' },
                   column: {
                     bodycell: ({ state }) => ({
                       style:  state['d_editing']&&'padding-top: 0.75rem; padding-bottom: 0.75rem'
                     })
                   }
                 }">
        <template #header>
          <div class="header-components gap-3">
            <Button
              icon="pi pi-plus"
              label="Add New Entity"
              class="p-button-sm"
              @click="dialogStore.toggle('add-entity')"
              rounded
            />
            <IconField>
              <InputText v-model="filters['global'].value" label="Keyword Search" aria-label="Keyword Search" placeholder="Keyword Search" />
              <InputIcon>
                <i class="pi pi-search" ></i>
              </InputIcon>
            </IconField>
          </div>
        </template>
        <template #empty> No Data. </template>
        <Column field="name" header="Entity Name" :sortable="true">
          <template #editor>
            <ValidatedInput
              id="name"
              label="Entity Name"
              v-model="rowForm.formData.name"
              :validation="rowForm.v$?.value?.name"
              :inputComponent="InputText"
              :inputAttrs="{
                placeholder: 'Entity Name',
                fluid: true
              }"
            />
          </template>
        </Column>

        <Column field="displayId" header="Entity ID" :sortable="true">
          <template #editor>
            <ValidatedInput
              v-if="rowForm.formData.type === 'PBM'"
              id="pbmId"
              label="PBM ID"
              v-model="rowForm.formData.pbmId"
              :validation="rowForm.v$?.value?.pbmId"
              :inputComponent="InputText"
              :inputAttrs="{ placeholder: 'PBM ID', fluid: true }"
            />
            <ValidatedInput
              v-else
              id="naicId"
              label="NAIC ID"
              v-model="rowForm.formData.naicId"
              :validation="rowForm.v$?.value?.naicId"
              :inputComponent="InputText"
              :inputAttrs="{ placeholder: 'NAIC ID', fluid: true }"
            />
          </template>

          <template #body="slotProps">
            {{ slotProps.data.pbmId ?? slotProps.data.naicId ?? '' }}
          </template>
        </Column>

        <Column field="type" header="Entity Type" :sortable="true">
          <template #editor>
            <ValidatedInput
              id="type"
              label="Entity Type"
              v-model="rowForm.formData.type"
              :validation="rowForm.v$?.value?.type"
              :inputComponent="Select"
              :inputAttrs="{
                options: entityTypes,
                optionLabel: 'label',
                optionValue: 'value',
                placeholder: 'Select Entity Type',
                fluid: true,
                optionTemplate: typeOptionTemplate
              }"
            />
          </template>

          <template #body="slotProps">
            <Tag :value="slotProps.data.type" :severity="getTypeSeverity(slotProps.data.type)"/>
          </template>
        </Column>

        <Column field="status" header="Status" :sortable="true">
          <template #editor>
            <ValidatedInput
              id="status"
              label="Status"
              v-model="rowForm.formData.status"
              :validation="rowForm.v$?.value?.status"
              :inputComponent="Select"
              :inputAttrs="{
                options: statuses,
                optionLabel: 'label',
                optionValue: 'value',
                placeholder: 'Select Status',
                fluid: true,
                optionTemplate: statusOptionTemplate
              }"
            />
          </template>

          <template #body="slotProps">
            <Tag
              :value="slotProps.data.status"
              :severity="getStatusLabel(slotProps.data.status)"
            />
          </template>
        </Column>

        <Column header="Actions" :rowEditor="true" style="width: 10%; min-width: 8rem" bodyStyle="text-align: center"/>
      </DataTable>
    </div>
  </form>

  <Dialog v-model:visible="isShowAddEntityOpen" modal header="Add New Entity" :closable="false" :style="{ width: '30rem' }">
    <AddEntityForm
      @entity-added="handleEntityAdded"
      @cancel="dialogStore.close('add-entity')"
    />
  </Dialog>
</template>

<script setup>
import {computed, h, ref, watch} from 'vue';
import PageHeader from "@/components/common/PageHeader.vue";
import {useAppStore} from "@/stores/appStore.js";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import Select from 'primevue/select';
import {ENTITY_STATUSES, ENTITY_TYPES} from "@/utils/constants.js";
import {updateEntity} from '@/services/entityService.js';
import { EntityModel } from "@/models/EntityModel.js";
import {useAuthStore} from "@/stores/authStore.js";
import AddEntityForm from "@/components/admin/AddEntityForm.vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Button from "primevue/button";
import {FilterMatchMode} from "@primevue/core/api";
import Dialog from "primevue/dialog";
import {useDialogStore} from "@/stores/dialogStore.js";
import ValidatedInput from "@/components/common/ValidatedInput.vue";
import {updateEntityValidationRules} from "@/validators/entities/updateEntityValidationRules.js";
import {useRowForm} from "@/composables/useRowForm.js";
import {useRowEditor} from "@/composables/useRowEditor.js";

const appStore = useAppStore();
const authStore = useAuthStore();
const dialogStore = useDialogStore();

const loading = ref(true);
const editingRows = ref([]);
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});
const rowForm = useRowForm(() =>
    updateEntityValidationRules(appStore.getEntitiesAll())
);

// use composable helpers for editor behavior
const { stopPrimeVueEvent, getCurrentRowId, reopenEditorForId, showToast } = useRowEditor(rowForm, editingRows);

const entities = computed(() =>
    (appStore.getEntitiesAll() ?? []).map(entity => ({
      ...entity,
    }))
);

const isShowAddEntityOpen = computed(() => dialogStore.isOpen('add-entity'));

const statuses = ref(
    Object.entries(ENTITY_STATUSES).map(([key, value]) => ({
      label: key,
      value: value,
    }))
);

const statusSeverity = {
  [ENTITY_STATUSES.Inactive]: 'warn',
  [ENTITY_STATUSES.Active]: 'success',
};

const getStatusLabel = status => statusSeverity[status] ?? null;

// Map entity types to tag severities so PBM can be a different color
const typeSeverity = {
  PBM: 'info',
};
const getTypeSeverity = (type) => typeSeverity[type] ?? null;

// Render template for type select options
const typeOptionTemplate = (slotProps) => {
  return h(Tag, { value: slotProps.option.value, severity: getTypeSeverity(slotProps.option.value) });
};

// Render template for status select options (moved out of template to avoid analyzer warnings)
const statusOptionTemplate = (slotProps) => {
  return h(Tag, { value: slotProps.option.value, severity: getStatusLabel(slotProps.option.value) });
};

const entityTypes = ref(
    Object.entries(ENTITY_TYPES).map(([key, value]) => ({
      label: key,
      value: value,
    }))
);

watch(
    () => appStore.loading,
    (isLoading) => {
      loading.value = isLoading;
    },
    { immediate: true }
);

const onRowEditInit = (event) => {
  const data = { ...event.data };
  // Ensure PBM/NAIC IDs exist
  data.pbmId = data.pbmId ?? null;
  data.naicId = data.naicId ?? null;
  rowForm.initRow(data);
};

const onRowEditCancel = () => {
  rowForm.cancel();
};

const onRowEditSave = async (event) => {
  // Stop PrimeVue's default close behavior synchronously
  stopPrimeVueEvent(event);

  // Validate the form
  const isValid = await rowForm.validate();

  if (!isValid) {
    showToast({ severity: 'warn', summary: 'Validation Error', detail: 'Please correct the errors before saving.' });
    // Keep the editor open for the current row
    await reopenEditorForId(getCurrentRowId());
    return;
  }

  // Commit the form back to the active row
  rowForm.commit();

  const normalized = rowForm.normalize((data) => ({
    ...data,
    // Ensure only the relevant ID is kept
    pbmId: data.type === 'PBM' ? data.pbmId : null,
    // Keep naicId for any non-PBM type (HMO, etc.)
    naicId: data.type !== 'PBM' ? data.naicId : null,
  }));

  // Safety guard: ensure non-PBM entities have naicId
  if (normalized.type !== 'PBM' && !normalized.naicId) {
    showToast({ severity: 'warn', summary: 'Validation Error', detail: 'NAIC ID is required for this entity type before saving.' });
    await reopenEditorForId(normalized?.id ?? getCurrentRowId());
    return;
  }

  // Perform the update and refresh entities
  try {
    const result = await updateEntity(new EntityModel(normalized));
    // Accept either a thrown error or a non-throwing failure result.
    const success = (result && (result.ok === true || result.success === true)) || result == null || typeof result === 'undefined' || !!result;
    if (!success) {
      // Non-throwing failure response from service - present error and reopen editor
      showToast({ severity: 'error', summary: 'Update Failed', detail: result?.message || 'Failed to update entity.' });
      await reopenEditorForId(getCurrentRowId());
      return;
    }

    await appStore.fetchAllEntities();
    // close the row editor since save succeeded
    editingRows.value = [];
    showToast({ severity: 'success', summary: 'Entity Updated', detail: `${normalized.name} updated successfully.` });
  } catch (err) {
    showToast({ severity: 'error', summary: 'Update Failed', detail: err?.message || 'Failed to update entity.' });
    // Reopen editor so user can try again
    await reopenEditorForId(getCurrentRowId());
  }
};

// Reload entities after adding
const reloadEntities = async () => {
  loading.value = true;
  try {
    await appStore.fetchAllEntities();
  } finally {
    loading.value = false;
  }
};

const handleEntityAdded = async () => {
  dialogStore.close("add-entity");
  await reloadEntities();
};

watch(() => rowForm.formData.type, (newType, oldType) => {
  if (!oldType) return; // skip on init
  if (newType !== oldType) {
    if (newType === 'PBM') rowForm.formData.naicId = null;
    else rowForm.formData.pbmId = null;
  }
});
</script>

<style scoped>
.header-components {
  display: flex;
  justify-content: flex-end;
}
</style>